import { CameraAlt, Close } from '@mui/icons-material';
import { Box, Button, CircularProgress, Modal, Stack, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";
import { fileUploadService } from '../services/s3-service';
import { notify } from './notify';

// Webcam document capture: live preview -> capture page -> add more pages ->
// Done uploads every captured page and returns the file keys to the caller.
function CameraCapture({ open, handleClose, onCaptured }) {
    const { t } = useTranslation();
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const [snapshot, setSnapshot] = useState(null); // dataURL of the frame under review
    const [pendingBlob, setPendingBlob] = useState(null); // blob of the frame under review
    const [pages, setPages] = useState([]);         // accepted page blobs
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;

        async function startCamera() {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
                });
                if (cancelled) {
                    stream.getTracks().forEach((tr) => tr.stop());
                    return;
                }
                streamRef.current = stream;
                if (videoRef.current) videoRef.current.srcObject = stream;
            } catch (err) {
                notify("error", t("bo.fileUpload.noCamera"));
                handleClose();
            }
        }
        startCamera();

        return () => {
            cancelled = true;
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((tr) => tr.stop());
                streamRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    function resetAndClose() {
        setSnapshot(null);
        setPages([]);
        handleClose();
    }

    function handleCapture() {
        const video = videoRef.current;
        if (!video || !video.videoWidth) return;
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d').drawImage(video, 0, 0);
        setSnapshot(canvas.toDataURL('image/jpeg', 0.92));
        canvas.toBlob((blob) => setPendingBlob(blob), 'image/jpeg', 0.92);
    }

    function handleRetake() {
        setSnapshot(null);
        setPendingBlob(null);
    }

    function acceptPage() {
        if (pendingBlob) setPages((prev) => [...prev, pendingBlob]);
        setSnapshot(null);
        setPendingBlob(null);
    }

    async function handleDone() {
        // Include the page currently under review, if any.
        const allPages = pendingBlob ? [...pages, pendingBlob] : pages;
        if (allPages.length === 0) {
            notify("info", t("bo.fileUpload.noPages"));
            return;
        }
        setUploading(true);
        try {
            const keys = [];
            for (let i = 0; i < allPages.length; i++) {
                const file = new File([allPages[i]], `camera-page-${i + 1}.jpg`, { type: 'image/jpeg' });
                keys.push(await fileUploadService(file));
            }
            onCaptured(keys);
            resetAndClose();
        } catch (err) {
            notify("error", "Error in File Uploading...");
        }
        setUploading(false);
    }

    return (
        <Modal open={open} onClose={resetAndClose}>
            <Box sx={modalStyle}>
                <Stack direction={'row'} justifyContent={"space-between"} alignItems={"center"} mb={1.5}>
                    <Typography sx={{ fontWeight: "bold", fontSize: 17, color: "rgba(0, 0, 0, 0.6)" }}>
                        {t("bo.fileUpload.captureTitle")}{pages.length > 0 ? ` (${pages.length} ${t("bo.fileUpload.pages")})` : ""}
                    </Typography>
                    <Button variant='text' startIcon={<Close />} onClick={resetAndClose}>{t("bo.updSts.cancel")}</Button>
                </Stack>

                <Box sx={{ position: "relative", backgroundColor: "#000", borderRadius: 2, overflow: "hidden", height: 420 }}>
                    <video ref={videoRef} autoPlay playsInline muted
                        style={{ width: "100%", height: "100%", objectFit: "contain", display: snapshot ? "none" : "block" }} />
                    {snapshot && <img src={snapshot} alt="captured page"
                        style={{ width: "100%", height: "100%", objectFit: "contain" }} />}
                </Box>

                <Stack direction={"row"} justifyContent={"center"} gap={2} mt={2}>
                    {!snapshot && <Button variant='contained' startIcon={<CameraAlt />} onClick={handleCapture} disabled={uploading}>
                        {t("bo.fileUpload.capture")}
                    </Button>}
                    {snapshot && <>
                        <Button variant='outlined' onClick={handleRetake} disabled={uploading}>{t("bo.fileUpload.retake")}</Button>
                        <Button variant='outlined' onClick={acceptPage} disabled={uploading}>{t("bo.fileUpload.addPage")}</Button>
                    </>}
                    {(pages.length > 0 || snapshot) &&
                        <Button variant='contained' color='success' onClick={handleDone} disabled={uploading}
                            startIcon={uploading ? <CircularProgress size={16} color='inherit' /> : null}>
                            {t("bo.fileUpload.done")}
                        </Button>}
                </Stack>
            </Box>
        </Modal>
    )
}

export default CameraCapture;

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 640,
    bgcolor: 'background.paper',
    border: '2px solid #ffff',
    boxShadow: 24,
    px: 3,
    py: 2,
    borderRadius: 2,
};
